import React, { useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useLocation } from "react-router-dom";
import { ChevronDown, UserCog, UserPlus, LayoutGrid } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import WorkspaceSwitcherMenu from "./WorkspaceSwitcherMenu";
import CreateWorkspaceModal from "./CreateWorkspaceModal";

function WorkspaceSwitcher({ user, onSwitch }) {
    const { t } = useTranslation();
    const { logout, isAdmin, isSuperAdmin, isClient } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [isOpen, setIsOpen] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const triggerRef = useRef(null);
    const companyId = user?.company?.id || user?.companyId;
    const logoUrl = companyId ? localStorage.getItem(`ws_logo_${companyId}`) : null;

    if (!user) return null;

    const activeCompany = user.companies?.find((c) => c.isActive)?.company || user.company;
    const companies = user.companies || [];

    const menuItems = [
        !isClient && { key: "account", icon: UserCog, label: t("sidebar.workspace.accountSettings"), to: "/profile" },
        !isClient && { key: "invite", icon: UserPlus, label: t("sidebar.workspace.inviteMembers"), to: "/profile/users" },
        { key: "integrations", icon: LayoutGrid, label: t("sidebar.workspace.appsIntegrations"), to: "/integrate" },
    ].filter(Boolean);

    const companyInitial = activeCompany?.name?.[0]?.toUpperCase() || "W";

    return (
        <>
            <div className="relative mb-4">
                <button
                    ref={triggerRef}
                    onClick={() => setIsOpen(!isOpen)}
                    className="w-full flex items-center gap-2 p-2 text-gray-900 rounded-lg hover:bg-gray-100 transition-colors"
                >
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center shrink-0 overflow-hidden ${logoUrl ? "bg-white border border-gray-200 p-1" : "bg-blue-600 text-white text-sm font-semibold"}`}>
                        {logoUrl
                            ? <img src={logoUrl} alt="" className="w-full h-full object-contain" />
                            : companyInitial}
                    </div>
                    <span className="text-sm font-medium truncate flex-1 text-left">
                        {activeCompany?.name || t("sidebar.workspace.selectWorkspace")}
                    </span>
                    <ChevronDown size={16} className={`text-gray-400 transition-transform ${isOpen ? "rotate-180" : ""}`} />
                </button>
                {isOpen && (
                    <WorkspaceSwitcherMenu
                        triggerRef={triggerRef}
                        companies={companies} menuItems={menuItems} onSwitch={onSwitch}
                        onClose={() => setIsOpen(false)}
                        onCreateWorkspace={() => { setIsOpen(false); setShowCreateModal(true); }}
                        onSignOut={logout} navigate={navigate} location={location} t={t}
                    />
                )}
            </div>
            <CreateWorkspaceModal open={showCreateModal} onOpenChange={setShowCreateModal} />
        </>
    );
}

export default WorkspaceSwitcher;
